import {
    ClientTextColor,
    InitialMessageText,
    InitialMessageWebhook,
    Language,
    OperatorNameTemplate
} from "./webChatConfig"
import { ChannelValuesBase } from "./channelValues"

export interface WebChatChannelValues extends ChannelValuesBase {
    AuthSettings: AuthSettingsValues
    AppearanceSettings: AppearanceValues
    MobileVersionEnabled: boolean
    HistorySettings: HistoryValues
    PushMessageSettings: PushMessageValues
    OperatorSettings: OperatorValues
    InterfaceSettings: InterfaceValues
    PromoSettings: PromoValues
    InitialMessageSettings: InitialMessageValues
    SyncSettings: SyncValues
    AuthAdvanced: AuthAdvancedValues
    FileSettings: FileValues
    DialogEvaluationSettings: DialogEvaluationValues
    BlockSettings: BlockValues
    SystemSettings: SystemValues
    MessageSettings: MessageValues
    SecuritySettings: SecurityValues
}

export interface AuthSettingsValues {
    type: AuthType
    useHash: boolean
    hashedFields: string[]
    isShownSalt: boolean
    salt: string
    requireFirstnameField: boolean
    requireLastnameField: boolean
    requirePhoneField: boolean
    requireWebhookAuth: boolean
    webhookAuthorizationUrl: string
}

export interface AppearanceValues {
    headerText: string
    headerDescription: string
    baseColor: string
    textColor: ClientTextColor
    language: Language
    darkTheme: boolean
    darkBaseColor: string
    darkTextColor: ClientTextColor
}

export interface SyncValues {
    enabled: boolean
    mediatorId: string
    namespace: string
    namespaceCobrowsing: string
}

export interface HistoryValues {
    showHistory: boolean
    historyMessageCount: number
    historyFromActiveDialog: boolean
    legacyHistoryApi: boolean
}

export interface PushMessageValues {
    notificationNewMessages: boolean
    pushMessageClientOnline: boolean
    pushMessageTitle: string
    pushMessageBody: string
    pushMessageBodyFromMessageText: boolean
    pushMessageIcon: string
    pushMessageTag: string
    pushMessageTimeout: number
    swPublicKey: string
    externalPushServiceUrl: string
    subscriptionRequestDelay: number
    swPublicKeySwitcher?: boolean
}

export interface OperatorValues {
    operatorIsTypingTimeout: number
    showConnectedOperatorNotify: boolean
    showOperatorName: boolean
    operatorNameTemplate: OperatorNameTemplate
    disableOperatorImage: boolean
}

export interface InterfaceValues {
    disableClosing: boolean
    disableHeader: boolean
    disableCompanyInformation: boolean
    useEmoji: boolean
    requireChatDraggable: boolean
    requireChatResizing: boolean
    logoColor: string
    alwaysOpen: boolean
    disableInitialOpen: boolean
    clickablePhoneNumber: boolean
    hideMessageInfo: boolean
    screensharingEnabled: boolean
    videocallEnabled: boolean
}

export interface PromoValues {
    showPromo: boolean
    promoTimeoutMin: number
    promoTimeoutSec: number
    promoWidth: number
    promoHeight: number
    promoAllowedUrls: string[]
    promoWelcomeMessage: string
}

export interface InitialMessageValues {
    mode: InitialMessageMode
    initialMessageOperatorName: string
    initialMessageText: InitialMessageText
    getInitialMessageFromWebhook: boolean
    initialMessageWebhook: InitialMessageWebhook
}

export interface AuthAdvancedValues {
    phoneNumberMask: string
    allowedSymbolsRegexp: string
    clearNonValidUserData: boolean
    requireAuthData: string[]
    userFormFields: string
    usePersonalDataAgreement: boolean
    personalDataText: string
    authButtonText: string
}

export interface FileValues {
    showImageDownloadLink: boolean
    allowedFileNameRegex: string
    fileRegexErrorMessage: string
    oversizedFileErrorMessage: string
    uploadSizeLimitValue: number
    uploadSizeLimitUnit: string
    useCaptcha: boolean
    captchaSecretKey: string
    captchaSiteKey: string
    captchaFileCount: number
    externalFilesUrl: string
    maxFileCount: number
    maxFileCountErrorMessage: string
    filesLinksCompatibility: boolean
}

export interface DialogEvaluationValues {
    ratingAnswerText: string
    showRatingBox: boolean
    requireScoreRequest: boolean
    questionBeforeRatingDialog: boolean
    ratingAliasesEnabled: boolean
    ratingAliases: string[]
    requireDialogFinishWithEstimate: boolean
}

export interface BlockValues {
    blockMessage: string
}

export interface SystemValues {
    socketIOInitialLoad: boolean
    cobrowsingEnabled: boolean
    bottomPlacement: boolean
    enableMobileVersion: boolean
    rootElementValue: string
    rootElementType: ElementType
    openChatButtonSelectorValue: string
    openChatButtonSelectorType: ElementType
    customCss: string
    iFrameMode: string
    iFrameHost: string
    debug: string
    customTransport: TransportType
    chatAnnouncement: string
    useSentry: boolean
    isDebugScenario: boolean
}

export interface MessageValues {
    sendUserIsTyping: boolean
    userTypingInterval: number
    forwardMessage: boolean
    showClientServiceMessage: boolean
    showFinishDialogByOperatorMessage: boolean
    finishDialogMessage: string
    linkOpeningBehaviour: LinkOpeningType
    locationMessage: string
    selectClickedButtons: boolean
    sendClickedButtons: boolean
}

export interface SecurityValues {
    extraSecurity: boolean
    securedFileLinks: boolean
}

export enum InitialMessageMode {
    sendInitialMessageOnOpen = "sendInitialMessageOnOpen",
    sendInitialMessageOnStartDialog = "sendInitialMessageOnStartDialog",
    showInitialMessage = "showInitialMessage"
}

export enum IFrameMode {
    disabled = "",
    insecure = "*",
    specifiedHost = "specifiedHost"
}

export enum ElementType {
    id = "id",
    class = "class",
    tag = "tag",
    custom = "custom"
}

export enum AuthType {
    Anonymously = "anonymously",
    RequireAuthFields = "requireAuthFields",
    JavaScriptAuthorization = "JavaScriptAuthorization"
}

export enum LinkOpeningType {
    sameTab = "sameTab",
    newTab = "newTab",
    newWindow = "newWindow"
}

export enum TransportType {
    websocket = "websocket",
    polling = "polling"
}
